// Structured Data Templates for SEO

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Interesting Technologies Private Limited",
  alternateName: "Interesting Technologies",
  url: "https://interestech.com",
  logo: "https://interestech.com/lovable-uploads/ITPL Logo.webp",
  image: "https://interestech.com/itpl-og.webp",
  description:
    "ISO 9001:2015 certified, government-recognized startup and Official Cloud Services Partner with AWS, Google Cloud, and Microsoft Azure, driving digital transformation for enterprises worldwide through innovative, world-class software solutions.",
  hasCertification: {
    "@type": "Certification",
    name: "ISO 9001:2015",
    certificationBody: "International Organization for Standardization",
    validFrom: "2024-01-01",
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: "3rd Floor, IT Bhavan, Indranagar",
    addressLocality: "Agartala",
    addressRegion: "West Tripura",
    postalCode: "799006",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-88119-46161",
    contactType: "customer service",
    email: "contact@interestech.com",
    availableLanguage: ["English", "Hindi"],
  },
  sameAs: ["https://www.linkedin.com/company/it-pvt-ltd"],
  founders: [
    {
      "@type": "Person",
      name: "Interesting Technologies Team",
    },
  ],
  foundingDate: "2022-04",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    value: 50,
  },
  slogan: "Transform your business with cutting-edge technology",
});

export const createLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Interesting Technologies Private Limited",
  alternateName: "Interesting Technologies",
  url: "https://interestech.com",
  logo: "https://interestech.com/lovable-uploads/ITPL Logo.webp",
  image: "https://interestech.com/itpl-og.webp",
  description:
    "ISO 9001:2015 certified, government-recognized startup and Official Cloud Services Partner with AWS, Google Cloud, and Microsoft Azure, driving digital transformation for enterprises worldwide through innovative, world-class software solutions.",
  hasCertification: {
    "@type": "Certification",
    name: "ISO 9001:2015",
    certificationBody: "International Organization for Standardization",
    validFrom: "2024-01-01",
  },
  priceRange: "$$",
  telephone: "+91-88119-46161",
  email: "contact@interestech.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "3rd Floor, IT Bhavan, Indranagar",
    addressLocality: "Agartala",
    addressRegion: "West Tripura",
    postalCode: "799006",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "23.8315",
    longitude: "91.2868",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "10:00",
      closes: "16:00",
    },
  ],
  sameAs: ["https://www.linkedin.com/company/it-pvt-ltd"],
  areaServed: {
    "@type": "GeoCircle",
    geoMidpoint: {
      "@type": "GeoCoordinates",
      latitude: "23.8315",
      longitude: "91.2868",
    },
    geoRadius: "100000",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "IT Services & Solutions",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Custom Software Development",
          description: "AI embedded software solutions for businesses",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "AI Integration Services",
          description: "AI-powered business transformation",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Cloud Solutions",
          description: "AWS, Google Cloud, and Azure services",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Mobile App Development",
          description: "iOS and Android application development",
        },
      },
    ],
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "50",
    bestRating: "5",
    worstRating: "1",
  },
});

export const createSoftwareProductSchema = (product: {
  name: string;
  description: string;
  url: string;
  image?: string;
  features?: string[];
  category?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: product.name,
  description: product.description,
  url: product.url,
  image: product.image || "https://interestech.com/lovable-uploads/ITPL Logo.webp",
  applicationCategory: product.category || "BusinessApplication",
  operatingSystem: "Web, iOS, Android",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  },
  provider: {
    "@type": "Organization",
    name: "Interesting Technologies Private Limited",
    url: "https://interestech.com",
  },
  featureList: product.features || [],
});

export const createCourseSchema = (course: {
  name: string;
  description: string;
  url: string;
  duration?: string;
  provider?: string;
  skills?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name: course.name,
  description: course.description,
  url: course.url,
  provider: {
    "@type": "Organization",
    name: course.provider || "IT Academy by Interesting Technologies",
    url: "https://interestech.com/placement-training",
  },
  educationalLevel: "Professional",
  timeRequired: course.duration || "4-7 weeks",
  availableLanguage: "English",
  teaches: course.skills || [],
  hasCourseInstance: {
    "@type": "CourseInstance",
    courseMode: "online",
    courseWorkload: course.duration || "PT4W",
  },
});

export const createServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
  serviceType: string;
  areaServed?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": service.url,
  name: service.name,
  description: service.description,
  url: service.url,
  serviceType: service.serviceType,
  provider: {
    "@type": "Organization",
    "@id": "https://interestech.com/#organization",
    name: "Interesting Technologies Private Limited",
    url: "https://interestech.com",
  },
  areaServed: service.areaServed || "Worldwide",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: service.name,
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          "@id": `${service.url}#service`,
          name: service.name,
        },
      },
    ],
  },
});

export const createJobPostingSchema = (job: {
  title: string;
  description: string;
  url: string;
  datePosted: string;
  employmentType?: string;
  location?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: job.title,
  description: job.description,
  url: job.url,
  datePosted: job.datePosted,
  employmentType: job.employmentType || "FULL_TIME",
  hiringOrganization: {
    "@type": "Organization",
    name: "Interesting Technologies Private Limited",
    sameAs: "https://interestech.com",
    logo: "https://interestech.com/lovable-uploads/ITPL Logo.webp",
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      streetAddress: "3rd Floor, IT Bhavan, Indranagar",
      addressLocality: "Agartala",
      addressRegion: "Tripura",
      postalCode: "799006",
      addressCountry: "IN",
    },
  },
});

export const createFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const createArticleSchema = (article: {
  headline: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.headline,
  description: article.description,
  url: article.url,
  datePublished: article.datePublished || new Date().toISOString(),
  dateModified: article.dateModified || new Date().toISOString(),
  author: {
    "@type": "Organization",
    name: article.author || "Interesting Technologies",
    url: "https://interestech.com",
  },
  publisher: {
    "@type": "Organization",
    name: "Interesting Technologies Private Limited",
    logo: {
      "@type": "ImageObject",
      url: "https://interestech.com/lovable-uploads/ITPL Logo.webp",
    },
  },
  image: article.image || "https://interestech.com/lovable-uploads/ITPL Logo.webp",
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": article.url,
  },
});

// Breadcrumb structured data for improved search appearance
export interface BreadcrumbItem {
  name: string;
  url: string;
}

export const createBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

// Predefined breadcrumb paths for common pages
export const getBreadcrumbsForPath = (pathname: string): BreadcrumbItem[] => {
  const baseUrl = "https://interestech.com";
  const home = { name: "Home", url: baseUrl };

  const pathSegments = pathname.split("/").filter(Boolean);

  if (pathSegments.length === 0) {
    return [home];
  }

  const breadcrumbs: BreadcrumbItem[] = [home];

  // Category mappings
  const categoryNames: Record<string, string> = {
    services: "Services",
    technologies: "Technologies",
    work: "Our Work",
    cloud: "Cloud Partners",
    curriculum: "Curriculum",
  };

  // Page name mappings
  const pageNames: Record<string, string> = {
    // Services
    "mvp-development": "MVP Development",
    "custom-development": "Custom Development",
    "cloud-solutions": "Cloud Solutions",
    "data-visualisation": "Data Visualisation",
    // Work

    "pos-plus": "POS Plus",
    "school-360": "School 360",
    textmi: "Textmi",
    // Cloud
    "amazon-aws": "Amazon AWS",
    "google-cloud": "Google Cloud",
    "microsoft-azure": "Microsoft Azure",
    // Curriculum
    "cpp-programming": "C++ Programming",
    "data-structures": "Data Structures",
    "data-structures-part-1": "Data Structures Part 1",
    "data-structures-part-2": "Data Structures Part 2",
    "computer-science": "Computer Science",
    "computer-science-part-1": "Computer Science Part 1",
    "computer-science-part-2": "Computer Science Part 2",
    "fullstack-mern": "Full Stack MERN",
    "fullstack-mern-part-1": "Full Stack MERN Part 1",
    "fullstack-mern-part-2": "Full Stack MERN Part 2",
    flutter: "Flutter",
    "flutter-part-1": "Flutter Part 1",
    "flutter-part-2": "Flutter Part 2",
    "machine-learning": "Machine Learning",
    "machine-learning-part-1": "Machine Learning Part 1",
    "machine-learning-part-2": "Machine Learning Part 2",
    // Main pages
    about: "About Us",
    contact: "Contact",
    "our-work": "Our Work",
    internship: "Internship",
    "internship-registration": "Internship Registration",
    "privacy-policy": "Privacy Policy",
    "terms-of-service": "Terms of Service",
  };

  let currentPath = "";

  for (const segment of pathSegments) {
    currentPath += `/${segment}`;

    // Check if it's a category
    if (categoryNames[segment]) {
      breadcrumbs.push({
        name: categoryNames[segment],
        url: `${baseUrl}${currentPath}`,
      });
    } else if (pageNames[segment]) {
      breadcrumbs.push({
        name: pageNames[segment],
        url: `${baseUrl}${currentPath}`,
      });
    } else {
      // Fallback: capitalize and format segment
      const formattedName = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      breadcrumbs.push({
        name: formattedName,
        url: `${baseUrl}${currentPath}`,
      });
    }
  }

  return breadcrumbs;
};
